// @ts-nocheck
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

import {
  useTableState,
  useTableDispatch,
} from '../../context/TableSchemaBuilderContext';
import ConfigColumnSidebar from './ConfigColumnSidebar';
import ConfigColumnDetails from './ConfigColumnDetails';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';

/**
 * Main layout container for configColumn mode.
 * Renders a split view:
 *   Left (250px) : ConfigColumnSidebar — column list, + button, search
 *   Right (flex)  : ConfigColumnDetails — attribute info for selected column
 *
 * Manages:
 *   - selectedColumnName  : which column is active in the sidebar
 *   - Attribute add/remove via dispatch to tableReducer
 */
const ConfigColumnBuilder = ({ attributeList = EMPTY_ARRAY }) => {
  const { schema } = useTableState();
  const dispatch = useTableDispatch();

  const [selectedColumnName, setSelectedColumnName] = useState(null);

  // ── Handlers ──────────────────────────────────────────────

  const handleSelectColumn = useCallback(
    (colName) => {
      setSelectedColumnName((prev) => (prev === colName ? prev : colName));
    },
    [setSelectedColumnName]
  );

  const handleAddAttribute = useCallback((attribute) => {
    dispatch({
      type: 'ADD_CONFIG_COLUMN_WITH_ATTRIBUTE',
      payload: { attribute },
    });
  }, []);

  const handleRemoveColumn = useCallback(
    (colName) => {
      // If the removed column was selected, clear the selection
      setSelectedColumnName((prev) => (prev === colName ? null : prev));
      dispatch({
        type: 'REMOVE_CONFIG_COLUMN',
        payload: { columnName: colName },
      });
    },
    [dispatch, setSelectedColumnName]
  );

  const handleReorderColumn = useCallback(
    (dragIndex, hoverIndex) => {
      dispatch({
        type: 'REORDER_CONFIG_COLUMN',
        payload: { dragIndex, hoverIndex },
      });
    },
    [dispatch]
  );

  // ── Render ─────────────────────────────────────────────────

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        minHeight: 380,
        overflow: 'hidden',
      }}
    >
      {/* Left: Sidebar */}
      <ConfigColumnSidebar
        schema={schema}
        attributeList={attributeList}
        selectedColumnName={selectedColumnName}
        onSelectColumn={handleSelectColumn}
        onAddAttribute={handleAddAttribute}
        onRemoveColumn={handleRemoveColumn}
        onReorderColumn={handleReorderColumn}
      />

      {/* Right: Attribute details only */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <ConfigColumnDetails
          selectedColumnName={selectedColumnName}
          schema={schema}
          attributeList={attributeList}
        />
      </Box>
    </Box>
  );
};

ConfigColumnBuilder.propTypes = {
  attributeList: PropTypes.arrayOf(PropTypes.shape({})),
};

export default ConfigColumnBuilder;
